/** Payment helpers shared by the Payment page, sign-up and sign-in. */
import { API_BASE } from "@/api/config";
import { ApiError, errorFrom } from "@/api/errors";
import { brand } from "@/brand";
import type { BadgeTone } from "@/components/ui";
import { formatDate } from "@/lib/format";

import type { Payment, PayTo, PlanDue } from "../account/platform";

export const METHOD_OPTIONS = [
  { value: "bank", label: "Bank transfer (NEFT / IMPS / RTGS)" },
  { value: "upi", label: "UPI" },
  { value: "other", label: "Other" },
] as const;

/** Today in India, as YYYY-MM-DD (the form's default payment date). */
export function todayIST(): string {
  return new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);
}

/** The upi:// deep link: payee, amount in rupees, INR, and a note. */
export function upiLink(payTo: PayTo, plan: PlanDue, note: string): string {
  const q = new URLSearchParams({
    pa: payTo.upi_id,
    pn: payTo.payee_name || brand.name,
    am: plan.total_amount.toFixed(2),
    cu: "INR",
    tn: note.slice(0, 80),
  });
  return `upi://pay?${q.toString()}`;
}

export interface PaymentBody {
  plan_code: string;
  amount: number;
  reference: string;
  paid_on: string;
  method: string;
}

/** What the API says when an account can't sign in yet (or a sign-up waits for payment). */
export interface InactiveDetail {
  code: "subscription_inactive";
  reason: string;
  message: string;
  payment_token: string;
  pay_to: PayTo;
  /** Bank transfer is switched on: show the bank details and the reference form. */
  bank_transfer?: boolean;
  plan: PlanDue | null;
  /** V068: PayU online payment is on. */
  online?: boolean;
}

/** POST …/payments/payu/start: PayU's hosted page and the form to post to it. */
export interface PayUStart {
  action: string;
  fields: Record<string, string>;
  txnid: string;
  amount: number;
}

export interface PayUStartBody {
  plan_code: string;
  months?: number;
  phone?: string;
}

/** Start a PayU checkout before the account can sign in (the payment token). */
export async function startWithToken(token: string, body: PayUStartBody): Promise<PayUStart> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/platform/payments/payu/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ ...body, payment_token: token }),
    });
  } catch {
    throw new ApiError(0, null);
  }
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as PayUStart;
}

/**
 * Send the browser to PayU's hosted page: a hidden form with the fields the
 * server made (the amount and hash come from the server, never from here).
 * Card, UPI and bank details are entered on PayU's page, not ours.
 */
export function goToPayU(start: PayUStart, doc: Document = document): void {
  const form = doc.createElement("form");
  form.method = "POST";
  form.action = start.action;
  form.style.display = "none";
  for (const [name, value] of Object.entries(start.fields)) {
    const input = doc.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  doc.body.appendChild(form);
  form.submit();
}

/** GET /platform/payments/payu/result: what the result page shows. */
export interface PayUResult {
  txnid: string;
  status: "created" | "pending" | "success" | "failed" | "cancelled";
  plan_name: string;
  months: number;
  amount: number;
  mihpayid: string | null;
  pay_mode: string | null;
  error_message: string | null;
  receipt_no: number | null;
  period_start: string | null;
  period_end: string | null;
  needs_review: boolean;
}

/** Submit a payment before the account can sign in (token from sign-up / sign-in). */
export async function submitWithToken(token: string, body: PaymentBody): Promise<Payment> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/platform/payments/submit`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ ...body, payment_token: token }),
    });
  } catch {
    throw new ApiError(0, null);
  }
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as Payment;
}

export function isInactiveDetail(d: unknown): d is InactiveDetail {
  return !!d && typeof d === "object" && (d as { code?: unknown }).code === "subscription_inactive";
}

/**
 * A subscription in words, from the stored kind and period (V033): "Trial ·
 * ends 22 Nov 2026", "Paid · until 22 Nov 2026", "Payment pending", "Ended".
 */
export function planStatus(
  kind: string | null | undefined,
  periodEnd: string | null | undefined,
  active: boolean | undefined,
): { label: string; tone: BadgeTone } {
  const end = periodEnd ? formatDate(periodEnd) : null;
  if (kind === "pending") return { label: "Payment pending", tone: "warn" };
  if (kind === "trial")
    return active === false
      ? { label: `Trial ended${end ? ` ${end}` : ""}`, tone: "down" }
      : { label: end ? `Trial · ends ${end}` : "Trial", tone: "accent" };
  if (kind === "paid")
    return active === false
      ? { label: `Ended${end ? ` ${end}` : ""}`, tone: "down" }
      : { label: end ? `Paid · until ${end}` : "Paid", tone: "up" };
  if (kind === "ended") return { label: "Ended", tone: "down" };
  return { label: "No plan", tone: "neutral" };
}

/**
 * An RA's client in words (V035): "RA client · Ravi K", "RA client · service
 * ended, inactive on 30 Oct 2026", "RA client · service ended".
 */
export function clientStatus(
  raName: string | null | undefined,
  status: string | null | undefined,
  inactiveOn: string | null | undefined,
): { label: string; tone: BadgeTone } {
  if (status === "ra_client_grace")
    return {
      label: `RA client · service ended, inactive on ${inactiveOn ? formatDate(inactiveOn) : "soon"}`,
      tone: "warn",
    };
  if (status === "ra_client_ended") return { label: "RA client · service ended", tone: "down" };
  return { label: `RA client · ${raName ?? "analyst"}`, tone: "brand" };
}
